import clsx from 'clsx';
import { useState, useMemo, useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useLocation, useNavigate } from 'react-router-dom';

import Loader from 'components/loader';
import Checkbox from 'components/checkbox';
import NotFound from 'components/not-found';
import TextInput from 'components/text-input';
import TabsScreen from 'components/tabs-screen';
import { ButtonProps } from 'components/button';
import RadioButton from 'components/radio-button';
import ItemsYmap, { MapItem } from 'components/items-ymap';
import ComplexBottomSheet from 'components/complex-bottom-sheet';
import { ChooseBuildingNavProps } from 'screens/choose-building';

import useBoundStore from 'store';
import useAppMode from 'hooks/use-app-mode';
import { BuyApartmentFlowPaths } from 'routes';
import { SellApartmentFlowPaths } from 'routes';
import useApartmentComplexSearch, { TComplexes } from 'api/hooks/use-apartment-complex-search';
import useViewApartmentDealsButtonProps from 'hooks/use-view-apartment-deals-button-props';
import { useApartmentSearch, useSetApartmentSearch, useSelectComplex } from 'store/selectors';
import styles from './styles.module.scss';
import ChooseMapPolygon from 'components/items-ymap/Polygon/ChooseMapPolygon';
import { MarketTypes } from 'api/types';
import useApartmentsFilterLimits from 'api/hooks/use-apartments-filter-limits';
import { IPolygonItemRequest } from 'api/hooks/use-apartments-limits-secondary';

const ChooseApartmentComplex: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const appMode = useAppMode();

  const search = useApartmentSearch(appMode);
  const setSearch = useSetApartmentSearch(appMode);
  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);
  const [tab, setTab] = useState(0);

  const { count, isLoading, filterLimits } = useApartmentsFilterLimits();

  useEffect(() => {
    if (location?.state?.prevUrl === 'buy/rooms-number') {
      setTab(1);
    }
  }, [location]);

  const viewApartmentDealsButtonProps = useViewApartmentDealsButtonProps(appMode === 'buy', tab);

  const buttons: ButtonProps[] =
    appMode === 'buy'
      ? [
          {
            variant: 'gray',
            ...viewApartmentDealsButtonProps,
          },
          {
            label: 'Продолжить',
            onClick: () => {
              navigate(
                BuyApartmentFlowPaths.chooseNumberOfRooms,
                //need improve it
                tab === 1 ? { state: { prevScreen: 'map' } } : undefined,
              );
            },
          },
        ]
      : [
          {
            label: 'Продолжить',
            disabled: selectedSellComplex.id === -1,
            onClick: () => {
              navigate(SellApartmentFlowPaths.chooseBuilding, {
                state: { defaultTab: 1 } as ChooseBuildingNavProps,
              });
            },
          },
        ];

  return (
    <TabsScreen
      buttons={buttons}
      onTabChange={setTab}
      title={appMode === 'buy' ? 'Подбор объектов' : 'Выберите ЖК'}
      noMarginForFloatingButtons
      fixedContentHeight={tab === 1}
      footerClassName={styles.footer}
      // floatingButtons={false}
      tabs={[
        { title: 'Списком', content: <ChooseApartmentComplexList /> },
        {
          title: 'На карте',
          content: (
            <ChooseMapPolygon
              type={MarketTypes.PRIMARY}
              polygonItems={(filterLimits?.polygons as IPolygonItemRequest[]) || []}
            />
          ),
        },
      ]}
      defaultTab={location?.state?.prevUrl === 'buy/rooms-number' ? 1 : 0}
      onBackButtonClick={() => navigate(-1)}
    >
      {tab === 0 && (
        <TextInput
          tall
          value={search}
          onChange={setSearch}
          placeholder='Поиск по ЖК'
          className={styles.mapSearchInput}
        />
      )}
    </TabsScreen>
  );
};

/* ------------------------------------------------------------------------------------ */

const ChooseApartmentComplexMap: React.FC = () => {
  const appMode = useAppMode();

  const { complexes } = useApartmentComplexSearch(appMode);

  const selectedComplexes = useBoundStore((s) => s.selectedComplexes);
  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [bottomSheetComplexId, setBottomSheetComplexId] = useState(0);

  const handleOpenBottomSheet = (id: number) => {
    setIsBottomSheetOpen(true);
    setBottomSheetComplexId(id);
  };

  const mapItems = useMemo(
    (): MapItem[] =>
      (complexes as TComplexes[]).flatMap((c) =>
        c.buildings.map((b) => ({
          id: c.id,
          selected:
            appMode === 'buy' ? selectedComplexes.has(c.id) : selectedSellComplex.id === c.id,
          coords: [b.latitude, b.longitude],
        })),
      ),
    [complexes, appMode, selectedComplexes, selectedSellComplex],
  );

  return (
    <>
      <div className={styles.tabContainer}>
        <ItemsYmap items={mapItems} onItemClick={(item) => handleOpenBottomSheet(item.id)} />
      </div>

      <ComplexBottomSheet
        open={isBottomSheetOpen}
        complexId={bottomSheetComplexId}
        onDismiss={() => setIsBottomSheetOpen(false)}
        onSpringEnd={(e) => e.type === 'CLOSE' && setBottomSheetComplexId(0)}
      />
    </>
  );
};

/* ------------------------------------------------------------------------------------ */

const ChooseApartmentComplexList: React.FC = () => {
  const appMode = useAppMode();

  const selectedComplexes = useBoundStore((s) => s.selectedComplexes);
  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);
  const selectComplex = useSelectComplex(appMode);

  const { complexes, isLoading } = useApartmentComplexSearch(appMode);

  const ListComponent = appMode === 'buy' ? Checkbox : RadioButton;

  return (
    <div className={styles.tabContainerList}>
      {complexes.length > 0 ? (
        <Virtuoso
          useWindowScroll
          data={complexes as TComplexes[]}
          style={{ height: '100%' }}
          itemContent={(idx, i) => {
            const checked =
              appMode === 'buy' ? selectedComplexes.has(i.id) : selectedSellComplex.id === i.id;

            return (
              <ListComponent
                label={i.title}
                className={clsx(
                  styles.checkbox,
                  idx === complexes.length - 1 && styles.checkboxLast,
                )}
                labelPosition='left'
                checked={checked}
                onChange={() => selectComplex(i.id, i.title)}
              />
            );
          }}
        />
      ) : isLoading ? (
        <Loader />
      ) : (
        <NotFound />
      )}
    </div>
  );
};

export default ChooseApartmentComplex;
